import { computed, inject, Injectable } from '@angular/core';
import { AppState } from '@app/reducers/interfaces';
import { DepartmentModel } from '@app/reducers/orm/department/department.model';
import { getActiveDepartments } from '@app/reducers/orm/department/department.service';
import { getActiveLocations, getLocationTree } from '@app/reducers/orm/location/location.service';
import { getTeamsWithoutFlexpoolGroupedByDepartment } from '@app/reducers/orm/team/team.service';
import { Store } from '@ngrx/store';
import { localeSort } from '@shiftbase-com/ts-shared/utilities';

@Injectable({ providedIn: 'root' })
export class HireService {
  private store = inject<Store<AppState>>(Store);

  private activeDepartments = this.store.selectSignal(getActiveDepartments);
  private teamsPerDepartment = this.store.selectSignal(getTeamsWithoutFlexpoolGroupedByDepartment());
  private activeLocations = this.store.selectSignal(getActiveLocations);

  teamsTree = computed(() => {
    const departmentEntities = this.activeDepartments();
    const teamsPerDepartment = this.teamsPerDepartment();
    const locations = this.activeLocations();

    const filteredTeamsPerDepartment = Object.fromEntries(
      Object.entries(teamsPerDepartment).map(([departmentId, teams]) => [
        departmentId,
        teams.filter((team) => !team.deleted),
      ]),
    );

    const locationsById = Object.fromEntries(locations.map((location) => [location.id, location]));

    const departments: DepartmentModel[] = Object.values(departmentEntities);
    return (
      getLocationTree(departments, locationsById, {
        items: filteredTeamsPerDepartment,
        valueField: 'name',
        idField: 'id',
      }) || []
    );
  });

  sortEmployees<T extends { first_name: string; last_name: string }>(
    employees: T[],
    sortField: string,
    sortDirection: string = 'ASC',
  ): T[] {
    const direction = sortDirection === 'ASC' ? 'asc' : 'desc';

    if (sortField === 'last_name') {
      return localeSort(employees, ['last_name', 'first_name'], [direction, 'asc']);
    }

    return localeSort(employees, ['first_name', 'last_name'], [direction, 'asc']);
  }
}
